// frontend/src/pages/BestPerformancesPage.js
//
// Best performances across a set of matches — every fixture of a series
// (keeps itself current as fixtures are played) or matches picked by hand.
//   Man of the series — most UCC points
//   Best batsman      — most runs
//   Best bowler       — most wickets
//   Best fielder      — catches (not as keeper) + run-outs
//   Best keeper       — catches behind + stumpings
// Equal figures are a tie: joint winners, and level players share a position.
//
// Look: a club honours board (navy, gold lettering) carries the winners; one
// quiet table below shows the full standings for whichever award is chosen.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';

const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const teams = (m) => (m.capt1_team && m.capt2_team) ? `${m.capt1_team} vs ${m.capt2_team}` : 'Match';

// One definition per award: board label, the figure that decides it, its
// unit, and the table columns shown under it.
const AWARDS = [
  { key: 'series_points', tab: 'Points',   label: 'Man of the series', value: r => r.points,     unit: 'pts',
    cols: [['Matches', r => r.matches], ['MOM', r => r.mom || '–']] },
  { key: 'batting',       tab: 'Batting',  label: 'Best batsman',      value: r => r.runs,       unit: 'runs',
    cols: [['Inns', r => r.matches], ['Best', r => r.best], ['SR', r => r.strike_rate], ['4s/6s', r => `${r.fours}/${r.sixes}`]] },
  { key: 'bowling',       tab: 'Bowling',  label: 'Best bowler',       value: r => r.wickets,    unit: 'wkts',
    cols: [['Overs', r => r.overs], ['Runs', r => r.runs], ['Econ', r => r.economy], ['Best', r => r.best]] },
  { key: 'fielding',      tab: 'Fielding', label: 'Best fielder',      value: r => r.dismissals, unit: 'dismissals',
    cols: [['Catches', r => r.outfield_catches], ['Run-outs', r => r.run_outs]] },
  { key: 'keeping',       tab: 'Keeping',  label: 'Best keeper',       value: r => r.dismissals, unit: 'dismissals',
    cols: [['Caught', r => r.caught_behind], ['Stumped', r => r.stumpings]] },
];

const winnersOf = (rows, value) => (rows || []).filter(r => rows.length && value(r) === value(rows[0]));

const CSS = `
.bp { max-width: 860px; margin: 0 auto; padding: 18px 14px 110px; font-family: var(--font-body, 'DM Sans', sans-serif); color: var(--text-dark, #111827); }
.bp h1 { font-family: var(--font-display, Rajdhani, sans-serif); font-weight: 700; font-size: 1.9rem; color: var(--navy, #1E3A6E); margin: 0 0 14px; letter-spacing: -0.01em; }
.bp-controls { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
.bp-seg { display: inline-flex; background: #eef1f6; border-radius: 10px; padding: 3px; }
.bp-seg button { border: 0; background: transparent; padding: 8px 14px; border-radius: 8px; font: 600 0.88rem var(--font-body, sans-serif); color: var(--navy, #1E3A6E); cursor: pointer; }
.bp-seg button[aria-pressed="true"] { background: var(--navy, #1E3A6E); color: #fff; }
.bp-select { flex: 1; min-width: 200px; padding: 9px 12px; border: 1px solid var(--border-mid, #D1D5DB); border-radius: 10px; font: 500 0.92rem var(--font-body, sans-serif); background: #fff; }
.bp-picker { background: #fff; border: 1px solid var(--border, #E5E7EB); border-radius: 12px; padding: 12px; margin-bottom: 16px; }
.bp-picker-top { display: flex; gap: 10px; align-items: center; margin-bottom: 10px; font-size: 0.82rem; color: var(--text-muted, #6B7280); }
.bp-chips { display: flex; flex-wrap: wrap; gap: 6px; max-height: 200px; overflow-y: auto; }
.bp-chip { border: 1px solid var(--border-mid, #D1D5DB); background: #fff; color: var(--text-mid, #374151); border-radius: 999px; padding: 6px 11px; font: 600 0.8rem var(--font-body, sans-serif); cursor: pointer; }
.bp-chip[aria-pressed="true"] { background: var(--navy, #1E3A6E); border-color: var(--navy, #1E3A6E); color: #fff; }
.bp-chip small { font-weight: 500; opacity: 0.75; margin-left: 4px; }
.bp-link { border: 0; background: none; color: #b91c1c; font: 600 0.8rem var(--font-body, sans-serif); cursor: pointer; }

.bp-board { background: linear-gradient(180deg, var(--navy, #1E3A6E) 0%, #16305c 100%); border-radius: 16px; padding: 18px 20px 14px; color: #fff; box-shadow: inset 0 0 0 1px rgba(245,197,24,0.35); }
.bp-board-title { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding-bottom: 10px; border-bottom: 1px solid rgba(245,197,24,0.35); margin-bottom: 4px; }
.bp-board-title h2 { margin: 0; font-family: var(--font-display, Rajdhani, sans-serif); font-weight: 700; font-size: 1.35rem; color: var(--gold, #F5C518); }
.bp-board-title span { font-size: 0.78rem; color: rgba(255,255,255,0.7); }
.bp-row { display: grid; grid-template-columns: 150px 1fr auto; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.08); }
.bp-row:last-child { border-bottom: 0; }
.bp-row .lbl { font-size: 0.82rem; color: rgba(245,197,24,0.85); font-weight: 600; }
.bp-row .who { font-family: var(--font-display, Rajdhani, sans-serif); font-weight: 700; font-size: 1.3rem; line-height: 1.15; }
.bp-row .who em { font-style: normal; font-weight: 500; font-size: 0.8rem; color: rgba(255,255,255,0.65); margin-left: 6px; }
.bp-row .fig { font-family: var(--font-display, Rajdhani, sans-serif); font-weight: 700; font-size: 1.5rem; color: var(--gold, #F5C518); text-align: right; white-space: nowrap; }
.bp-row .fig small { font-size: 0.8rem; font-weight: 600; color: rgba(245,197,24,0.75); margin-left: 3px; }
.bp-row .none { color: rgba(255,255,255,0.45); font-size: 0.9rem; }
.bp-note { font-size: 0.75rem; color: rgba(255,255,255,0.55); padding-top: 8px; }

.bp-tabs { display: flex; gap: 2px; margin: 22px 0 0; border-bottom: 2px solid var(--border, #E5E7EB); overflow-x: auto; }
.bp-tabs button { border: 0; background: none; padding: 10px 14px; font: 600 0.9rem var(--font-body, sans-serif); color: var(--text-muted, #6B7280); cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; white-space: nowrap; }
.bp-tabs button[aria-selected="true"] { color: var(--navy, #1E3A6E); border-bottom-color: var(--gold, #F5C518); }
.bp-table { width: 100%; border-collapse: collapse; background: #fff; font-size: 0.88rem; }
.bp-table th { text-align: right; font-weight: 600; font-size: 0.75rem; color: var(--text-muted, #6B7280); padding: 10px 10px 8px; border-bottom: 1px solid var(--border, #E5E7EB); }
.bp-table th.l, .bp-table td.l { text-align: left; }
.bp-table td { text-align: right; padding: 10px; border-bottom: 1px solid #f3f4f6; color: var(--text-mid, #374151); font-variant-numeric: tabular-nums; }
.bp-table td.pos { width: 34px; text-align: left; color: var(--text-faint, #9CA3AF); font-weight: 600; }
.bp-table td.name { font-weight: 600; color: var(--text-dark, #111827); padding-left: 2px; }
.bp-table td.fig { font-family: var(--font-display, Rajdhani, sans-serif); font-weight: 700; font-size: 1.1rem; color: var(--navy, #1E3A6E); }
.bp-table tr.top td { background: var(--gold-bg, #FFFDF0); }
.bp-table-wrap { overflow-x: auto; border: 1px solid var(--border, #E5E7EB); border-top: 0; border-radius: 0 0 12px 12px; }
.bp-empty { padding: 18px; color: var(--text-muted, #6B7280); font-size: 0.88rem; background: #fff; }

.bp-matches { margin-top: 20px; font-size: 0.82rem; color: var(--text-muted, #6B7280); }
.bp-matches h3 { font: 600 0.88rem var(--font-body, sans-serif); color: var(--text-mid, #374151); margin: 0 0 8px; }
.bp-mlist { display: flex; flex-wrap: wrap; gap: 6px; }
.bp-m { padding: 5px 10px; border-radius: 8px; background: #fff; border: 1px solid var(--border, #E5E7EB); }
.bp-m.done { border-color: rgba(22,163,74,0.35); color: #166534; }
.bp-m.wait { color: var(--text-faint, #9CA3AF); border-style: dashed; }

.bp button:focus-visible, .bp select:focus-visible { outline: 2px solid var(--gold, #F5C518); outline-offset: 2px; }

@media (max-width: 560px) {
  .bp-row { grid-template-columns: 1fr auto; row-gap: 2px; }
  .bp-row .lbl { grid-column: 1 / -1; }
  .bp-row .who { font-size: 1.15rem; }
  .bp-board { padding: 14px 14px 10px; }
  .bp-tabs button { padding: 10px 10px; font-size: 0.85rem; }
  .bp-table td, .bp-table th { padding-left: 6px; padding-right: 6px; }
}
`;

// publicMode: the public site (/club/honours) reads the same figures from
// the anonymous endpoints in routes/public.js.
export default function BestPerformancesPage({ publicMode = false }) {
  const EP = publicMode
    ? { series: '/public/series', awards: '/public/series-awards', matches: '/public/series-awards/matches', seriesParams: {} }
    : { series: '/series', awards: '/series/awards', matches: '/series/awards/matches', seriesParams: { all: 1 } };
  const [params, setParams] = useSearchParams();
  const [mode, setMode]         = useState(params.get('matches') ? 'pick' : 'series');
  const [seriesList, setSeries] = useState([]);
  const [seriesId, setSeriesId] = useState(params.get('series') || '');
  const [year, setYear]         = useState(new Date().getFullYear());
  const [pickable, setPickable] = useState([]);
  const [picked, setPicked]     = useState(new Set((params.get('matches') || '').split(',').filter(Boolean)));
  const [data, setData]         = useState(null);
  const [err, setErr]           = useState(null);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    api.get(EP.series, { params: EP.seriesParams }).then(r => {
      const list = r.data.series || [];
      setSeries(list);
      if (!seriesId && list[0]) setSeriesId(list[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== 'pick') return;
    api.get(EP.matches, { params: { year } }).then(r => setPickable(r.data.matches || [])).catch(() => setPickable([]));
  }, [mode, year, EP.matches]);

  const pickedKey = useMemo(() => [...picked].sort().join(','), [picked]);

  const load = useCallback(async (quiet) => {
    const q = mode === 'series' ? (seriesId ? { series_id: seriesId } : null)
                                : (pickedKey ? { match_ids: pickedKey } : null);
    if (!q) { setData(null); return; }
    if (!quiet) setLoading(true);
    setErr(null);
    try { const r = await api.get(EP.awards, { params: q }); setData(r.data); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to load'); }
    finally { if (!quiet) setLoading(false); }
  }, [mode, seriesId, pickedKey, EP.awards]);

  useEffect(() => { load(false); }, [load]);

  // Series mode keeps itself current: a fixture appears the moment it has
  // figures, so check again every minute while the page is open.
  useEffect(() => {
    if (mode !== 'series') return undefined;
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [mode, load]);

  // Keep the choice in the address so it can be shared.
  useEffect(() => {
    const next = {};
    if (mode === 'series' && seriesId) next.series = seriesId;
    if (mode === 'pick' && pickedKey) next.matches = pickedKey;
    setParams(next, { replace: true });
  }, [mode, seriesId, pickedKey, setParams]);

  const toggle = (id) => setPicked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const [tab, setTab] = useState('series_points');
  const award = AWARDS.find(a => a.key === tab);
  const rows = data ? (data[award.key] || []) : [];
  const pos = (r) => 1 + rows.filter(x => award.value(x) > award.value(r)).length;
  const boardTitle = mode === 'series'
    ? (data?.series?.name || 'Series')
    : `${picked.size} selected match${picked.size === 1 ? '' : 'es'}`;

  return (
    <div className="bp">
      <style>{CSS}</style>
      <h1>Best performances</h1>

      <div className="bp-controls">
        <div className="bp-seg" role="group" aria-label="Choose matches by">
          <button aria-pressed={mode === 'series'} onClick={() => setMode('series')}>By series</button>
          <button aria-pressed={mode === 'pick'} onClick={() => setMode('pick')}>Pick matches</button>
        </div>
        {mode === 'series' && (
          <select className="bp-select" value={seriesId} onChange={e => setSeriesId(e.target.value)} aria-label="Series">
            {seriesList.length === 0 && <option value="">No series yet</option>}
            {seriesList.map(s => <option key={s.id} value={s.id}>{String(s.name).includes(String(s.year)) ? s.name : `${s.name} (${s.year})`}</option>)}
          </select>
        )}
      </div>

      {mode === 'pick' && (
        <div className="bp-picker">
          <div className="bp-picker-top">
            <select className="bp-select" style={{ flex: '0 0 auto', minWidth: 0 }} value={year}
              onChange={e => setYear(parseInt(e.target.value, 10))} aria-label="Year">
              {Array.from({ length: new Date().getFullYear() - 2012 }, (_, k) => new Date().getFullYear() - k).map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span style={{ flex: 1 }}>Tap the matches to include</span>
            {picked.size > 0 && <button className="bp-link" onClick={() => setPicked(new Set())}>Clear</button>}
          </div>
          <div className="bp-chips">
            {pickable.length === 0 && <span>No scorecards in {year} yet.</span>}
            {pickable.map(m => (
              <button key={m.id} className="bp-chip" aria-pressed={picked.has(m.id)} onClick={() => toggle(m.id)} title={teams(m)}>
                {fmtDate(m.match_date)}{m.series_name && <small>{m.series_name}</small>}
              </button>
            ))}
          </div>
        </div>
      )}

      {err && <div style={{ color: '#b91c1c', fontSize: '0.88rem', marginBottom: 12 }}>{err}</div>}
      {!data && !err && (
        <div className="bp-empty" style={{ borderRadius: 12, border: '1px solid var(--border, #E5E7EB)' }}>
          {loading ? 'Loading…' : mode === 'pick' ? 'Pick one or more matches to see the best performances across them.' : 'Choose a series.'}
        </div>
      )}

      {data && (
        <>
          <section className="bp-board" aria-label="Winners">
            <div className="bp-board-title">
              <h2>{boardTitle}</h2>
              <span>{data.counted} of {data.matches.length} played</span>
            </div>
            {AWARDS.map(a => {
              const list = data[a.key] || [];
              const w = winnersOf(list, a.value);
              return (
                <div className="bp-row" key={a.key}>
                  <div className="lbl">{a.label}</div>
                  {w.length ? (
                    <>
                      <div className="who">{w.map(x => x.name).join(' & ')}{w.length > 1 && <em>joint</em>}</div>
                      <div className="fig">{a.value(w[0])}<small>{a.unit}</small></div>
                    </>
                  ) : <div className="none">Not yet</div>}
                </div>
              );
            })}
            {mode === 'series' && <div className="bp-note">Updates by itself as each fixture is played.</div>}
          </section>

          <div className="bp-tabs" role="tablist" aria-label="Standings">
            {AWARDS.map(a => (
              <button key={a.key} role="tab" aria-selected={tab === a.key} onClick={() => setTab(a.key)}>{a.tab}</button>
            ))}
          </div>
          <div className="bp-table-wrap">
            {rows.length === 0 ? <div className="bp-empty">Nothing recorded for this yet.</div> : (
              <table className="bp-table">
                <thead>
                  <tr>
                    <th className="l" colSpan={2}>Player</th>
                    <th>{award.unit === 'pts' ? 'Points' : award.unit === 'runs' ? 'Runs' : award.unit === 'wkts' ? 'Wickets' : 'Dismissals'}</th>
                    {award.cols.map(([h]) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.member_id} className={pos(r) === 1 ? 'top' : ''}>
                      <td className="pos">{pos(r)}</td>
                      <td className="l name">{r.name}</td>
                      <td className="fig">{award.value(r)}</td>
                      {award.cols.map(([h, f]) => <td key={h}>{f(r)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bp-matches">
            <h3>Matches counted</h3>
            <div className="bp-mlist">
              {data.matches.map(m => (
                <span key={m.id} className={`bp-m ${m.counted ? 'done' : 'wait'}`} title={`${teams(m)}${m.source ? ` (${m.source})` : ''}`}>
                  {fmtDate(m.match_date)} {m.counted ? '✓' : '— to play'}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
